import React, { useCallback, useEffect, useMemo } from 'react';
import { css, Theme, useTheme } from "@emotion/react";
import { Helmet } from "react-helmet-async";
import { isEmpty } from "lodash";
import { unstable_useBlocker as useBlocker, useNavigate } from "react-router-dom";

import { getSiteTitle } from "core/config/config";
import { useAppState } from "core/contexts/AppContext";
import { FieldList } from "core/hooks/useForm";
import useHeaderHeight from "core/hooks/useHeaderHeight";
import useSetState from "core/hooks/useSetState";

import PageSection from "pages/Components/CartAndCheckout/components/PageSection";
import DetailsForm from "pages/Components/CartAndCheckout/components/DetailsForm";
import HeaderTitle from "pages/Components/CartAndCheckout/components/HeaderTitle";
import PageLeftSection from "pages/Components/CartAndCheckout/components/PageLeftSection";
import PageRightSection from "pages/Components/CartAndCheckout/components/PageRightSection";
import useCartDetailSizes from "pages/Components/CartAndCheckout/hooks/useCartDetailSizes";
import CheckoutPagination from "pages/Components/CartAndCheckout/components/CheckoutPagination";
import PaymentComponent from "pages/Components/CartAndCheckout/components/PaymentComponent";
import Billing from "pages/Components/CartAndCheckout/components/Billing";
import CartSummaryContainer from "pages/Components/CartAndCheckout/components/CartSummaryContainer";
import useDetailsForms, { BILLING_FIELDS_TO_USE } from "pages/Components/CartAndCheckout/hooks/useDetailsForms";
import usePlaceOrder from "pages/Components/CartAndCheckout/hooks/usePlaceOrder";
import PaymentButton from "pages/Components/CartAndCheckout/components/PaymentButton";
import CheckoutFinishDialog from './components/CheckoutFinishDialog';
import useCart from "pages/Components/CartPage/hooks/useCart";
import useShipping from "pages/Components/CartAndCheckout/hooks/useShipping";

type Props = {
    showBreadcrumb: boolean,
    hideHeaderCountry: boolean,
}

type DefaultState = {
    checkoutStage: number,
    paymentOption: string,
    isUsingShippingAddress: boolean,
    isSubmitting: boolean,
    orderNumber: string,
    isErrorDialogOpen: boolean,
    shippingOption: any,
    suburbOptions?: string[],
};

const INITIAL_STATE = {
    checkoutStage: 0,
    paymentOption: "Invoice",
    isUsingShippingAddress: true,
    isSubmitting: false,
    orderNumber: '',
    isErrorDialogOpen: false,
    shippingOption: {},
    suburbOptions: undefined,
};

const DEFAULT_PAGE_TITLE = 'Checkout';

const CheckoutPage: React.FC<Props> = ({ showBreadcrumb, hideHeaderCountry }) => {
    const theme: Theme = useTheme();
    const [state, setState] = useSetState<DefaultState>(INITIAL_STATE);
    const [appState, setAppState] = useAppState();
    const headerHeight = useHeaderHeight({ showBreadcrumb, hideHeaderCountry });
    const locationOrigin = window.location.origin;
    const { paddingTop } = useCartDetailSizes();
    const { fields, errors, onFieldChange, validate } = useDetailsForms({
        validateBillingFields: state.checkoutStage === 2 && !state.isUsingShippingAddress,
    });
    const { getCountry } = useCart();
    const navigate: any = useNavigate();

    const countryCode = getCountry();

    const filteredFields = useMemo(() => {
        const contactFields: FieldList = {};
        const fieldsForBilling: FieldList = {};
        const billingFields: FieldList = {};
        const readOnlyFields: string[] = [];

        Object.keys(fields).forEach(key => {
            if (!key.includes('Billing')) {
                contactFields[key] = fields[key];
            }

            if (BILLING_FIELDS_TO_USE.includes(key)) {
                const billingKey = `${key}Billing`;
                billingFields[billingKey] = fields[billingKey];

                // Don't need address search input field for read only fields
                if (key === 'addressSearch') return;
                fieldsForBilling[key] = fields[key];
            }
        });

        if (countryCode.code.toLowerCase() === 'nz') {
            readOnlyFields.push('country');
        }

        return {
            fields: fieldsForBilling,
            billingFields,
            contactFields,
            readOnlyFields,
        }
    }, [countryCode.code, fields]);

    const { clearCartStorage, getLocalStorageCartUuid } = useCart();
    const { packageProducts, removeShipping } = useShipping({ filteredFields });

    useBlocker(() => {
        if (getLocalStorageCartUuid()) {
            removeShipping();
            setState({ shippingOption: {} });
        }
        return false;
    });

    const {
        paymentSubmit,
        sendInvoice,
        state: orderState,
        setState: setOrderState,
    } = usePlaceOrder({
        fields: state.isUsingShippingAddress ? filteredFields.fields: filteredFields.billingFields,
        detailFields: filteredFields.contactFields,
        shippingOption: state.shippingOption,
    });

    const onPaymentOptionSelect = useCallback((option: string) => setState({ paymentOption: option }), [setState]);

    const onAddressChange = useCallback((usingShippingAddress = false) => {
        if (usingShippingAddress !== state.isUsingShippingAddress) {
            setState({ isUsingShippingAddress: usingShippingAddress });
        }
    }, [setState, state.isUsingShippingAddress]);

    // Update the field 'city' to the suggested suburb name.
    const setSuggestedSuburb = useCallback((suburbName: string) => {
        onFieldChange('city', suburbName);
    }, [onFieldChange]);

    // Remove the suggested suburb name.
    const clearSuburbOptions = useCallback(() => {
        setState({ suburbOptions: undefined });
    }, [setState]);

    const isInvoice = state.paymentOption === 'Invoice';

    const paginationSteps = useMemo(() => [
        {
            label: 'Detail',
            component: <DetailsForm
                fields={filteredFields.contactFields}
                readOnlyFields={filteredFields.readOnlyFields}
                errors={errors}
                onFieldChange={onFieldChange}
                suburbOptions={state.suburbOptions}
                setSuggestedSuburb={setSuggestedSuburb}
                clearSuburbOptions={clearSuburbOptions}
                hasAsterisk
            />,
            headerTitle: 'Your details',
        },
        {
            label: 'Payment method',
            component: <PaymentComponent onSelect={onPaymentOptionSelect} selectedOption={state.paymentOption} />,
            headerTitle: 'Choose your payment method',
        },
        {
            label: 'Billing',
            component: <Billing
                fields={filteredFields}
                isInvoice={isInvoice}
                onFieldChange={onFieldChange}
                errors={errors}
                orderState={orderState}
                setOrderState={setOrderState}
                isUsingShippingAddress={state.isUsingShippingAddress}
                onAddressChange={onAddressChange}
            />,
            headerTitle: isInvoice ? 'Send invoice' : 'Payment',
            ...isInvoice && {
                subHeaderTitle: ' to your email',
            }
        }
    ], [
        errors,
        filteredFields,
        onAddressChange,
        onFieldChange,
        onPaymentOptionSelect,
        orderState,
        setOrderState,
        state.isUsingShippingAddress,
        state.paymentOption,
        state.suburbOptions,
        setSuggestedSuburb,
        clearSuburbOptions,
        isInvoice,
    ]);

    const validateFields = useCallback(() => {
        if (state.checkoutStage && state.checkoutStage !== paginationSteps.length - 1) return true;

        return validate();
    }, [paginationSteps.length, state.checkoutStage, validate]);

    const onNextStep = useCallback(async() => {
        if (!validateFields()) return;

        const hasProduct = !!appState.cart?.products?.length;
        const hasPhysicalProducts = !!appState.cart?.products?.filter((product: CartProduct) => product.type !== 'event').length;

        let shippingPackage: any = {};

        if (!state.checkoutStage && hasProduct && hasPhysicalProducts) {
            await packageProducts().then((res) => {
                shippingPackage = res;
                setState({
                    ...shippingPackage.shippingOptions.length ? {
                        shippingOption: shippingPackage.shippingOption
                    } : {
                        suburbOptions: shippingPackage.suburbOptions
                    }
                });
            });
        }

        setState((prev: DefaultState) => {
            const hasShipping = (prev.checkoutStage === 0 && !!shippingPackage.shippingOptions?.length) ||
                (prev.checkoutStage === 1 && (!isEmpty(shippingPackage.shippingOption) || !isEmpty(prev.shippingOption)));

            return {
                ...prev,
                isSubmitting: false,
                ...((!hasProduct || hasShipping || !hasPhysicalProducts) && prev.checkoutStage < paginationSteps.length - 1) && {
                    checkoutStage: Math.min(prev.checkoutStage + 1, paginationSteps.length - 1)
                }
            };
        })
    }, [validateFields, appState.cart?.products, state.checkoutStage, setState, packageProducts, paginationSteps.length]);

    const onBackStep = useCallback(() => {
        // Current checkoutStage is 0 and back button was clicked: Move to the Cart page
        if (state.checkoutStage === 0) {
            setAppState((prevState: any) => ({
                cart: {
                    ...prevState.cart,
                    shippingAmount: 0
                }
            }));
            navigate("/cart-summary");
        } else {
            setState((prevState: DefaultState) => ({
                checkoutStage: Math.max(prevState.checkoutStage - 1, 0)
            }));
        }
    }, [state.checkoutStage, setAppState, setState, navigate]);

    const onStepClick = useCallback((step: number) => setState({ checkoutStage: step }), [setState]);

    const component = paginationSteps[state.checkoutStage].component;

    const onClearCart = useCallback(() => {
        clearCartStorage()
    }, [clearCartStorage]);

    const onPlaceOrder = useCallback(() => {
        setState({ isSubmitting: true });
        // Check the available quantity for the products on the cart
        if (!validateFields()) {
            setState({ isSubmitting: false });
            return;
        }

        // Return array definition
        // sendInvoice => 0: createDraftOrderResponse, 1: sendOrderInvoiceResponse
        // paymentSubmit => 0: paymentResponse, 1: createDraftOrderResponse, 2: completeOrderResponse
        const mutation = isInvoice ? sendInvoice : paymentSubmit;
        const responseDataIndex = isInvoice ? 1 : 2;
        let isErrorDialogOpen = true;

        mutation()
            .then((response: any) => {
                if (response === 'card-error') {
                    isErrorDialogOpen = false;
                    throw new Error('Card error.');
                }

                const responseData = response[responseDataIndex];
                const data = isInvoice ? responseData?.data?.sendOrderInvoice : responseData?.data?.completeOrder;

                setState({
                    orderNumber: data || "",
                    isSubmitting: false,
                    isErrorDialogOpen: !data
                });
            })
            .catch((error) => {
                console.error(error);
                setState({
                    isErrorDialogOpen,
                    isSubmitting: false,
                });
            });
    }, [setState, validateFields, isInvoice, sendInvoice, paymentSubmit]);

    const isLast = state.checkoutStage === paginationSteps.length - 1;

    const isDialogOpen = state.isErrorDialogOpen || !!state.orderNumber;
    const isErrorDialog = isDialogOpen && (state.isErrorDialogOpen);
    const isInvoiceDialog = isDialogOpen && (isInvoice && !isErrorDialog);
    const isPaymentDialog = isDialogOpen && (!isInvoice && !isErrorDialog);
    const isSuccessDialog = isDialogOpen && (isInvoiceDialog || isPaymentDialog);

    const bottomSectionMessageTwo = useMemo(() => {
        switch(true) {
            case (isErrorDialog):
                return 'Your payment cannot be completed.';
            case (isInvoiceDialog):
                return 'A copy of your invoice and order number has been sent to your email.';
            case (isPaymentDialog):
                return 'Your order details have been sent to your email.';
            default:
                return '';
        }
    }, [isErrorDialog, isInvoiceDialog, isPaymentDialog]);

    // Set the breadcrumb label
    useEffect(() => {
        setAppState({
            currentPageName: DEFAULT_PAGE_TITLE,
            loading: false,
        });
    }, [setAppState]);

    // Reset error if payment method changes
    useEffect(() => {
        setOrderState({
            errorMessage: '',
            isVisibleErrorMessage: false,
        });
    }, [isInvoice, setOrderState]);

    useEffect(() => {
        if (!state.isSubmitting &&
            !isDialogOpen &&
            !state.orderNumber &&(
            !getLocalStorageCartUuid() || (
                !appState.isCartLoading &&
                !appState.cart
            )
        )) {
            navigate('/');
        }
    }, [appState.cart, navigate, getLocalStorageCartUuid, appState.isCartLoading, state.isSubmitting, state.orderNumber, isDialogOpen]);

    const leftSectionStyle = css`
        flex-direction: column;
        justify-content: space-between;

        ${theme.breakpoints.up('3xl')} {
            padding: ${paddingTop}px 0 37px 0;
        }

        ${theme.breakpoints.only('xxl')} {
            padding: ${paddingTop}px 0 58px 0;
        }

        ${theme.breakpoints.only('xl')} {
            padding: ${paddingTop}px 0 48px 0;
        }

        ${theme.breakpoints.only('lg')} {
            padding: ${paddingTop}px 0 35px 0;
        }

        ${theme.breakpoints.only('md')} {
            padding: ${paddingTop}px 0 26px 0;
        }

        ${theme.breakpoints.only('sm')} {
            padding: ${paddingTop}px 0 27px 0;
        }

        ${theme.breakpoints.down('xs')} {
            padding: ${paddingTop}px 11px 34px 12px;
        }
    `;

    const wrapperStyle = css`
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        margin-left: auto;

        ${theme.breakpoints.up('3xl')} {
            margin-right: 195px;
        }

        ${theme.breakpoints.up('xl')} {
            max-width: 618px;
        }

        ${theme.breakpoints.only('xxl')} {
            margin-right: 150px;
        }

        ${theme.breakpoints.only('xl')} {
            margin-right: 56px;
        }

        ${theme.breakpoints.only('lg')} {
            max-width: 581px;
            margin-right: 38px;
        }

        ${theme.breakpoints.only('lg')} {
            margin-right: 37px;
        }

        ${theme.breakpoints.only('md')} {
            max-width: 604px;
            margin-right: auto;
        }

        ${theme.breakpoints.only('sm')} {
            max-width: 494px;
            margin-right: auto;
        }

        ${theme.breakpoints.down('xs')} {
            max-width: 100%;
            margin-left: 0;
            margin-right: 0;
        }
    `;

    const headerStyle = css`
        justify-content: start;
        padding-bottom: 19px;
        font-weight: ${theme.fonts.weights.normal};
    `;

    const pageRightSectionStyle = css`
        height: 100%;
    `;

    const messageTwoStyle = css`
        .bottom-section-messages {
            width: 300px;
            
            div {
                width: 100%;
            }
        }
    `;

    return (
        <>
            <Helmet>
                {locationOrigin && (
                    <link rel="canonical" href={locationOrigin} />
                )}
                <title>{getSiteTitle("Checkout")}</title>
            </Helmet>
            <PageSection headerHeight={headerHeight}>
                {isDialogOpen && (
                    <CheckoutFinishDialog
                        heading={isErrorDialog ? 'Oh No!' : 'Thank You!'}
                        buttonText={isErrorDialog ? 'Try again' : 'Continue shopping'}
                        bottomSectionMessageTwo={bottomSectionMessageTwo}
                        {...isSuccessDialog && {
                            bottomSectionMessageOne: `Order Number: #${state.orderNumber}`,
                            closeTo: '/',
                            onClose: () => onClearCart(),
                            onClickButton: () => onClearCart(),
                        }}
                        {...isInvoiceDialog && {
                            css: messageTwoStyle,
                            bottomColour: theme.colours.malibu5,
                            circleIcon: 'email',
                        }}
                        {...isPaymentDialog && {
                            subheading: 'Your payment was successful.',
                        }}
                        {...isErrorDialog && {
                            subheading: `Something's gone wrong.`,
                            css: messageTwoStyle,
                            bottomColour: theme.colours.red1,
                            circleIcon: 'exclamation',
                            buttonTo: '',
                            onClose: () => {
                                setState({ isErrorDialogOpen: false })
                            },
                            onClickButton: () => {
                                setState({ isErrorDialogOpen: false });
                                onPlaceOrder();
                            }
                        }}
                    />
                )}
                <PageLeftSection css={leftSectionStyle}>
                    <div css={wrapperStyle}>
                        <HeaderTitle textColour={'black'} css={headerStyle}>
                            {paginationSteps[state.checkoutStage].headerTitle}&nbsp;
                            {paginationSteps[state.checkoutStage].subHeaderTitle}
                        </HeaderTitle>
                        {component}
                        <CheckoutPagination
                            steps={paginationSteps.map(step => step.label)}
                            currentStep={state.checkoutStage}
                            onNext={onNextStep}
                            onBack={onBackStep}
                            onStepClick={onStepClick}
                        />
                    </div>
                </PageLeftSection>
                <PageRightSection paddingTop={paddingTop} css={pageRightSectionStyle}>
                    <CartSummaryContainer
                        {...isLast && !isSuccessDialog && {
                            button: (
                                <PaymentButton
                                    onClick={onPlaceOrder}
                                    loading={state.isSubmitting}
                                    label={state.paymentOption === 'Invoice' ? 'Send invoice' : 'Place order'}
                                />
                            )
                        }}
                        isCheckout
                    />
                </PageRightSection>
            </PageSection>
        </>
    )
};

export default CheckoutPage;